import { FragmentOf, graphql, readFragment } from 'gql.tada'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useMutation } from 'urql'

import IconDelete from '@/assets/icons/ic_tart_delete.svg'
import {
  WebPanel,
  WebPanelItem,
  WebPanelItemContent,
  WebPanelItemLabel,
} from '@/components/web-panel'
import { Maybe } from '@/utils/common-types'

import { DeletePlayerConfirmModal } from '../modal'
import { PSI_guardianDeletePlayerMutation } from './PlanSettings'

interface Props {
  viewer: Maybe<FragmentOf<typeof CurriculumPlayerList_viewer>>
}

enum EModal {
  None,
  PlayerDeleteConfirm,
}

export const CurriculumPlayerList = (props: Props): JSX.Element => {
  const [modal, setModal] = useState<EModal>(EModal.None)
  const [deletePlayerId, setDeletePlayerId] = useState<string>('')
  const viewer = readFragment(CurriculumPlayerList_viewer, props.viewer)
  const onlySubscription =
    (viewer?.hasSubscription || viewer?.isFreeTrialUser) && !viewer?.hasCurriculumLicense

  const [_, deletePlayer] = useMutation(PSI_guardianDeletePlayerMutation)
  const handleDeletePlayer = async () => {
    const { data, error } = await deletePlayer({
      input: { id: deletePlayerId },
    })

    setDeletePlayerId('')
    setModal(EModal.None)

    if (!data || error || data.playerDelete?.errors?.length) {
      return toast.error('プレイヤーの削除に失敗しました', {
        toastId: 'player-delete-failed',
        position: 'bottom-right',
      })
    }

    return toast.success('プレイヤーを削除しました', {
      toastId: 'player-delete-successfully',
      position: 'bottom-right',
    })
  }

  if (onlySubscription) return <></>

  return (
    <section className="mx-auto mt-10 max-w-[940px]">
      {viewer?.players.length ? (
        <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
          プレイヤー一覧
        </h3>
      ) : null}

      <div className="relative grid gap-[20px] md:gap-[34px] lg:gap-[40px]">
        {viewer?.players.map((player, index) => (
          <section key={player.id}>
            <div className="mb-3 flex justify-between">
              <h4 className="text-xs font-bold text-secondary-text md:text-[13px] lg:text-[15px]">
                プレイヤー{index + 1}: {player.nickname ?? '未設定'}
              </h4>
              <button
                type="button"
                onClick={() => {
                  setDeletePlayerId(player.id)
                  setModal(EModal.PlayerDeleteConfirm)
                }}
                className="flex items-center text-xs font-bold text-primary-100 disabled:text-outline md:text-[14px] disabled:[&_svg]:text-outline"
                disabled={!player.canDelete}
              >
                <IconDelete className="w-[20px] md:w-[24px]" />
                プレイヤーを削除
              </button>
            </div>
            <WebPanel>
              {player.ownCurriculums?.length ? (
                <>
                  {player.ownCurriculums?.map((c) => (
                    <WebPanelItem key={c.id} className="py-2.5">
                      <WebPanelItemLabel>利用中の教材</WebPanelItemLabel>
                      <WebPanelItemContent>
                        <p className="text-sm">{c.name}</p>
                      </WebPanelItemContent>
                    </WebPanelItem>
                  ))}
                </>
              ) : (
                <WebPanelItem className="py-2.5">
                  <WebPanelItemLabel>利用中の教材</WebPanelItemLabel>
                  <WebPanelItemContent>
                    <p className="text-sm">なし</p>
                  </WebPanelItemContent>
                </WebPanelItem>
              )}
            </WebPanel>
          </section>
        ))}
      </div>

      <DeletePlayerConfirmModal
        isOpen={modal === EModal.PlayerDeleteConfirm}
        onOk={handleDeletePlayer}
        onRequestClose={() => {
          setDeletePlayerId('')
          setModal(EModal.None)
        }}
      />
    </section>
  )
}

export const CurriculumPlayerList_viewer = graphql(`
  fragment CurriculumPlayerList_viewer on User {
    id
    hasCurriculumLicense
    hasSubscription
    isFreeTrialUser
    players {
      id
      name
      nickname
      subscriptionInfo {
        id
        status
      }
      ownCurriculums {
        id
        name
      }
      canDelete
    }
  }
`)
